import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import styles from './RecallFlowView.module.scss'
import { speak } from '../speech/speak'
import voices from '../speech/voices'
import recognitionLangs from '../speech/recognitionLangs'
import languages from '../languages'
import { celebrate } from './juice'
import { tokenizeLine, tokenizeTranscript, tokenMatches, romanizationOf, cancelTts } from '../speech/flowMatch'

import CloseIcon from '@mui/icons-material/Close'
import MicIcon from '@mui/icons-material/Mic'
import MicOffIcon from '@mui/icons-material/MicOff'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import ReplayIcon from '@mui/icons-material/Replay'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition

// Drop an embedded Latin gloss like "케이팝(K-pop)은" → "케이팝은". The "(K-pop)"
// isn't meant to be spoken; it just adds Latin tokens the Korean STT then hears
// as English, which the learner can never match.
const stripGloss = (s) => String(s || '').replace(/\(([^)]*[A-Za-z][^)]*)\)/g, '').replace(/\s{2,}/g, ' ').trim()

// Prompt with English, produce the non-English side (mirrors Flow's side pick).
const practiceSideOf = (card) => {
  const t = card.translations?.[0]
  if (card.sourceLanguage && card.sourceLanguage !== 'eng' && card.source) {
    return { text: stripGloss(card.source), lang: card.sourceLanguage, prompt: t?.target || '' }
  }
  if (t?.target && t.targetLanguage !== 'eng') {
    return { text: stripGloss(t.target), lang: t.targetLanguage, prompt: card.source || '' }
  }
  return { text: stripGloss(card.source), lang: card.sourceLanguage || 'eng', prompt: t?.target || '' }
}

// After this many *no-progress* tries at a fade level, one hidden word quietly
// comes back as a scaffold. Any newly recalled word resets the count, so taking
// your time to work through a sentence never triggers it — and it never wipes
// progress or advances on its own.
const MISS_BEFORE_REVEAL = 3

export default function RecallFlowView({ cards, voiceSpeed, onClose }) {
  const lines = useMemo(
    () => (cards || []).map(practiceSideOf).filter(i => i.text && i.text.trim()),
    [cards]
  )

  const [idx, setIdx] = useState(0)
  const [hits, setHits] = useState({ forIdx: -1, states: [] }) // per word: bool
  const [maskLevel, setMaskLevel] = useState(0)                 // how many words hidden
  const [maskOrder, setMaskOrder] = useState([])                // order words hide in
  const [listening, setListening] = useState(false)
  const [interim, setInterim] = useState('')
  const [micError, setMicError] = useState(null)
  const [micPhase, setMicPhase] = useState('off')
  const [peek, setPeek] = useState(false)                       // temporarily show everything
  const [showRoman, setShowRoman] = useState(true)              // romanization under each word
  const [feedback, setFeedback] = useState(null)                // { type, text } toast

  const recRef = useRef(null)
  const listeningRef = useRef(false)
  const completedRef = useRef(false)
  const sessionRef = useRef(0)
  const deadSessionsRef = useRef(0)
  const advanceTimerRef = useRef(null)
  const fbTimerRef = useRef(null)
  const missRef = useRef(0)
  const lastFinalRef = useRef('')
  const lineRef = useRef(null)
  const cardRef = useRef(null)
  // Source of truth for matching, updated synchronously so a mid-burst
  // recognition result always builds on the latest hits (React state lags).
  const liveHitsRef = useRef([])

  const line = lines[idx]
  const words = useMemo(() => tokenizeLine(line?.text, line?.lang), [line])
  const states = hits.forIdx === idx ? hits.states : []
  useEffect(() => { lineRef.current = line }, [line])

  // Per-word romanization (Korean only, via the deterministic library). Kept
  // per word so a hidden word can hide its romanization too — a full-line
  // romanization would leak the words we're asking the learner to recall.
  const wordRoman = useMemo(() => words.map(w => romanizationOf(w.raw, line?.lang)), [words, line])

  // Reset everything when the sentence changes: full-visible, fresh hide order.
  useEffect(() => {
    const order = words.map((_, i) => i)
    for (let i = order.length - 1; i > 0; i--) { // shuffle (Fisher–Yates)
      const j = Math.floor(Math.random() * (i + 1));
      [order[i], order[j]] = [order[j], order[i]]
    }
    setMaskOrder(order)
    setMaskLevel(0)
    const fresh = words.map(() => false)
    liveHitsRef.current = fresh
    setHits({ forIdx: idx, states: fresh })
    setInterim('')
    setPeek(false)
    completedRef.current = false
    missRef.current = 0
    lastFinalRef.current = ''
  }, [idx, words])

  const hiddenSet = useMemo(() => new Set(maskOrder.slice(0, maskLevel)), [maskOrder, maskLevel])

  const flashFeedback = useCallback((type, text) => {
    setFeedback({ type, text })
    clearTimeout(fbTimerRef.current)
    fbTimerRef.current = setTimeout(() => setFeedback(null), 1500)
  }, [])

  const advance = useCallback((dir = 1) => {
    clearTimeout(advanceTimerRef.current)
    setIdx(prev => {
      const next = prev + dir
      if (next < 0 || next >= lines.length) return prev
      return next
    })
  }, [lines.length])

  // ── recognizer (ported from Flow) ────────────────────────────────────────
  const applyTranscript = useCallback((transcript, isFinal) => {
    if (!lineRef.current) return
    const tokens = tokenizeTranscript(transcript, lineRef.current.lang)
    const base = liveHitsRef.current.length === words.length ? liveHitsRef.current : words.map(() => false)
    const next = [...base]
    let wi = 0
    let added = 0
    for (const tok of tokens) {
      for (let j = wi; j < Math.min(next.length, wi + 4); j++) {
        if (next[j]) { if (j === wi) wi++; continue }
        if (tokenMatches(tok, words[j]?.norm)) { next[j] = true; added++; wi = j + 1; break }
      }
    }
    liveHitsRef.current = next
    setHits({ forIdx: idx, states: next })

    // On a completed phrase: if it recalled a NEW word, they're progressing —
    // stay out of the way and reset patience. If it added nothing (genuinely
    // stuck), count it; only after MISS_BEFORE_REVEAL such tries does one hidden
    // word quietly come back. Progress is never wiped; there is no time limit.
    if (isFinal && !completedRef.current) {
      const heard = transcript.trim()
      if (heard && heard !== lastFinalRef.current) {
        lastFinalRef.current = heard
        if (added > 0) {
          missRef.current = 0
        } else {
          missRef.current += 1
          if (missRef.current >= MISS_BEFORE_REVEAL) {
            missRef.current = 0
            setMaskLevel(l => Math.max(0, l - 1))
          }
        }
      }
    }
  }, [idx, words])

  const stopListening = useCallback(() => {
    listeningRef.current = false
    setListening(false)
    setMicPhase('off')
    sessionRef.current++
    try { recRef.current?.stop() } catch { /* already stopped */ }
  }, [])

  const startListening = useCallback(() => {
    if (!SpeechRecognition) { setMicError('Speech recognition is not supported in this browser.'); return }
    if (!lineRef.current) return
    setMicError(null)
    cancelTts()
    const session = ++sessionRef.current
    const rec = new SpeechRecognition()
    rec.lang = recognitionLangs[lineRef.current.lang] || 'en-US'
    rec.continuous = true
    rec.interimResults = true
    let heardSound = false

    const stopWithError = (msg) => { listeningRef.current = false; setListening(false); setMicPhase('off'); setMicError(msg) }

    rec.onaudiostart = () => { if (session === sessionRef.current) setMicPhase('open') }
    rec.onsoundstart = () => { if (session === sessionRef.current) { heardSound = true; deadSessionsRef.current = 0; setMicPhase('sound') } }
    rec.onspeechstart = () => { if (session === sessionRef.current) { heardSound = true; deadSessionsRef.current = 0; setMicPhase('speech') } }
    rec.onresult = (event) => {
      if (session !== sessionRef.current) return
      heardSound = true
      deadSessionsRef.current = 0
      setMicPhase('speech')
      let transcript = ''
      let sawFinal = false
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript + ' '
        if (event.results[i].isFinal) sawFinal = true
      }
      setInterim(transcript.trim().slice(-70))
      applyTranscript(transcript, sawFinal)
    }
    rec.onerror = (e) => {
      if (session !== sessionRef.current) return
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') stopWithError('Microphone access was blocked — allow it in browser settings.')
      else if (e.error === 'audio-capture') stopWithError('No audio device captured — pick the right microphone in browser settings.')
      else if (e.error === 'language-not-supported') stopWithError(`This browser can't recognize ${rec.lang} speech.`)
      else if (e.error === 'network') setMicError('Speech service network error — retrying…')
    }
    rec.onend = () => {
      if (session !== sessionRef.current) return
      if (!listeningRef.current) { setMicPhase('off'); return }
      if (completedRef.current) return
      if (!heardSound) {
        deadSessionsRef.current++
        if (deadSessionsRef.current >= 4) {
          stopWithError('Mic is open but no sound is reaching it. Click the 🔒/camera icon in the address bar, pick your real microphone, then tap the mic again.')
          return
        }
      }
      setTimeout(() => { if (session === sessionRef.current && listeningRef.current) { try { rec.start() } catch { /* raced */ } } }, 500)
    }

    recRef.current = rec
    listeningRef.current = true
    deadSessionsRef.current = 0
    setListening(true)
    try { rec.start() } catch {
      setTimeout(() => { if (session === sessionRef.current && listeningRef.current) { try { rec.start() } catch { /* give up */ } } }, 400)
    }
  }, [applyTranscript])

  // Clear the cumulative transcript and start a fresh attempt on the SAME line.
  // Only used at fade-up (new challenge level) — a wrong try never calls this.
  const resetRep = useCallback(() => {
    completedRef.current = false
    lastFinalRef.current = ''
    missRef.current = 0
    const fresh = words.map(() => false)
    liveHitsRef.current = fresh
    setHits({ forIdx: idx, states: fresh })
    setInterim('')
    if (listeningRef.current) {
      sessionRef.current++
      try { recRef.current?.abort() } catch { /* fine */ }
      setTimeout(() => { if (listeningRef.current) startListening() }, 250)
    }
  }, [idx, words, startListening])

  // All words recalled → fade one more, or master the line if all were hidden.
  useEffect(() => {
    if (hits.forIdx !== idx) return
    if (words.length === 0 || hits.states.length !== words.length) return
    if (!hits.states.every(Boolean) || completedRef.current) return
    completedRef.current = true

    const el = cardRef.current
    if (el) {
      const r = el.getBoundingClientRect()
      celebrate(r.left + r.width / 2, r.top + r.height / 2, { count: 22, spread: 150 })
    }

    const nextLevel = maskLevel + 1
    if (nextLevel > words.length) {
      // The fully-hidden pass just succeeded — recalled from the English alone.
      flashFeedback('master', '★ From memory!')
      advanceTimerRef.current = setTimeout(() => advance(1), 1400)
    } else {
      setMaskLevel(nextLevel)
      missRef.current = 0
      flashFeedback('up', nextLevel >= words.length ? 'All hidden — say it from memory' : 'Nice — one more word hidden')
      setTimeout(resetRep, 750)
    }
  }, [hits, idx, words.length, maskLevel, advance, flashFeedback, resetRep])

  // Recycle recognition when the sentence changes while the mic is on.
  useEffect(() => {
    if (!listeningRef.current) return
    sessionRef.current++
    try { recRef.current?.abort() } catch { /* fine */ }
    const t = setTimeout(() => { if (listeningRef.current) startListening() }, 350)
    return () => clearTimeout(t)
  }, [idx]) // eslint-disable-line

  useEffect(() => () => {
    clearTimeout(advanceTimerRef.current)
    clearTimeout(fbTimerRef.current)
    listeningRef.current = false
    sessionRef.current++
    try { recRef.current?.abort() } catch { /* fine */ }
  }, [])

  const speakLine = () => { if (line) speak(line.text, voices[line.lang] || voices['eng'], voiceSpeed) }
  const revealOne = () => setMaskLevel(l => Math.max(0, l - 1))
  const hideOne = () => setMaskLevel(l => Math.min(words.length, l + 1))

  const handleWordClick = (i) => {
    if (!line) return
    speak(words[i].raw, voices[line.lang] || voices['eng'], voiceSpeed)
  }

  // Escape hatch: some words (loanwords like 케이팝 / "K-pop") get transcribed by
  // the Korean recogniser as English, so the mic can never match them. Double-tap
  // the word to accept it as recalled so it never blocks finishing the sentence.
  const acceptWord = (i) => {
    const base = liveHitsRef.current.length === words.length ? liveHitsRef.current : words.map(() => false)
    const next = [...base]
    next[i] = true
    liveHitsRef.current = next
    setHits({ forIdx: idx, states: next })
  }

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') advance(1)
      if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') advance(-1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [advance])

  if (lines.length === 0) {
    return (
      <div className={styles.view}>
        <button className={styles.closeBtn} onClick={onClose}><CloseIcon /></button>
        <div className={styles.empty}>Nothing to recall.</div>
      </div>
    )
  }

  const recLangLabel = recognitionLangs[line?.lang] || 'en-US'

  return (
    <div className={styles.view}>
      <button className={styles.closeBtn} onClick={onClose}><CloseIcon /></button>

      <div className={styles.topBar}>
        <span className={styles.modeLabel}>Recall · {languages[line?.lang] || line?.lang}</span>
        <label className={styles.toggle}>
          <input type="checkbox" checked={showRoman} onChange={e => setShowRoman(e.target.checked)} />
          romanization
        </label>
        <button
          className={styles.peekBtn}
          onMouseDown={() => setPeek(true)} onMouseUp={() => setPeek(false)} onMouseLeave={() => setPeek(false)}
          onTouchStart={() => setPeek(true)} onTouchEnd={() => setPeek(false)}
          title="Hold to peek at the full sentence"
        >
          {peek ? <VisibilityIcon style={{ fontSize: 18 }} /> : <VisibilityOffIcon style={{ fontSize: 18 }} />} peek
        </button>
      </div>

      <div className={styles.stage}>
        <div className={styles.promptLabel}>say this in {languages[line?.lang] || line?.lang}</div>
        <div className={styles.prompt}>{line?.prompt || '—'}</div>

        <div className={styles.card} ref={cardRef}>
          <div className={styles.lineWords}>
            {words.map((w, wi) => {
              const isHit = states[wi]
              const isHidden = hiddenSet.has(wi) && !isHit && !peek
              const glyphCls = [
                styles.word,
                isHit ? styles.wordHit : '',
                isHidden ? styles.wordHidden : '',
              ].join(' ')
              const roma = wordRoman[wi]
              const renderRoman = (showRoman || peek) && roma
              return (
                <div
                  key={wi}
                  className={styles.wordCol}
                  onClick={(e) => { e.stopPropagation(); handleWordClick(wi) }}
                  onDoubleClick={(e) => { e.stopPropagation(); acceptWord(wi) }}
                  title="Tap to hear · double-tap to accept a word the mic can't catch"
                >
                  <span className={glyphCls}>{w.raw}</span>
                  {renderRoman && (
                    <span className={[
                      styles.wordRoman,
                      isHidden ? styles.wordRomanHidden : '',
                      isHit ? styles.wordRomanHit : '',
                    ].join(' ')}>{roma}</span>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        <div className={styles.fadeMeter}>
          {words.map((_, i) => (
            <span key={i} className={`${styles.pip} ${hiddenSet.has(i) ? styles.pipHidden : ''}`} />
          ))}
          <span className={styles.fadeLabel}>{maskLevel}/{words.length} hidden</span>
        </div>

        <div className={styles.tip}>take your time — double-tap a word the mic keeps mishearing to accept it</div>

        {feedback && (
          <div className={`${styles.toast} ${styles[`toast_${feedback.type}`] || ''}`}>{feedback.text}</div>
        )}
      </div>

      <div className={styles.bottomBar}>
        <div className={styles.interim}>
          {micError
            ? micError
            : listening
              ? (interim || (micPhase === 'speech' ? `hearing you… (${recLangLabel})` : micPhase === 'sound' ? `picking up sound…` : `mic open — say it in ${languages[line?.lang] || line?.lang}`))
              : 'tap the mic and say it out loud'}
        </div>
        <div className={styles.controls}>
          <button className={styles.sideBtn} onClick={() => advance(-1)} disabled={idx === 0} title="Previous"><KeyboardArrowUpIcon /></button>
          <button className={styles.sideBtn} onClick={revealOne} disabled={maskLevel === 0} title="Reveal a word"><VisibilityIcon /></button>
          <button className={styles.sideBtn} onClick={speakLine} title="Hear it"><VolumeUpIcon /></button>
          <button
            className={`${styles.micBtn} ${listening ? styles.micBtnActive : ''}`}
            onClick={() => (listening ? stopListening() : startListening())}
            title={listening ? 'Stop the mic' : 'Say it out loud'}
          >
            {listening ? <MicIcon style={{ fontSize: 34 }} /> : <MicOffIcon style={{ fontSize: 34 }} />}
          </button>
          <button className={styles.sideBtn} onClick={hideOne} disabled={maskLevel >= words.length} title="Hide a word"><VisibilityOffIcon /></button>
          <button className={styles.sideBtn} onClick={resetRep} title="Restart this line"><ReplayIcon /></button>
          <button className={styles.sideBtn} onClick={() => advance(1)} disabled={idx === lines.length - 1} title="Next"><KeyboardArrowDownIcon /></button>
        </div>
        <div className={styles.progress}>{idx + 1} / {lines.length}</div>
      </div>
    </div>
  )
}
